import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Settings,
  TicketPercent,
  MessageSquare,
  Handshake,
  Users,
  Shield,
  Mail,
  Briefcase,
  ShoppingBag,
  Images,
  FileText,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/admin/overview', label: 'Dashboard', icon: Shield, end: true },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/admin/app-users', label: 'App Accounts', icon: Users },
      { to: '/admin/users', label: 'AI Submissions', icon: Sparkles },
      { to: '/admin/careers', label: 'Stylist Applications', icon: Briefcase },
      { to: '/admin/partnerships', label: 'Partnerships', icon: Handshake },
      { to: '/admin/contact', label: 'Contact Inquiries', icon: Mail },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/reports', label: 'Reports', icon: FileText },
      { to: '/admin/expert-requests', label: 'Expert Requests', icon: Sparkles },
      { to: '/admin/shopping', label: 'Shopping Bookings', icon: ShoppingBag },
      { to: '/admin/ai-results', label: 'AI Analyses', icon: Sparkles },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/admin/moodboard', label: 'Moodboard Library', icon: Images },
      { to: '/admin/testimonials', label: 'Testimonials', icon: MessageSquare },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/coupons', label: 'Coupons', icon: TicketPercent },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/app-ops', label: 'Settings', icon: Settings },
    ],
  },
];

const AdminLayout: React.FC = () => {
  const { currentUser, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-off-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-xl border border-light-gray overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-[70vh]">
            <aside className="border-b lg:border-b-0 lg:border-r border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-coral rounded-xl flex items-center justify-center">
                  <Shield size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-nunito font-bold text-charcoal leading-tight">Admin</div>
                  <div className="text-xs text-gray-600 truncate max-w-[190px]">
                    {currentUser?.email || currentUser?.displayName || 'Signed in'}
                  </div>
                </div>
              </div>

              <nav className="mt-6 space-y-5">
                {NAV_GROUPS.map((group) => (
                  <div key={group.label}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-3 mb-1.5">
                      {group.label}
                    </div>
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-2 rounded-xl transition-colors duration-200 ${
                                isActive ? 'bg-coral/10 text-coral' : 'text-gray-700 hover:bg-light-gray'
                              }`
                            }
                          >
                            <Icon size={17} />
                            <span className="font-medium text-sm">{item.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>

              <button
                onClick={() => logout()}
                className="mt-6 w-full px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-light-gray transition-colors duration-200"
              >
                Sign Out
              </button>
            </aside>

            <main className="p-6 lg:p-8">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
